/**
 * Utility functions for handling image URLs with CDN support
 */

/**
 * Get the backend base URL dynamically
 */
const getBackendUrl = (): string => {
  if (process.env.REACT_APP_BACKEND_BASE_URL && process.env.REACT_APP_BACKEND_BASE_URL !== "/") {
    return process.env.REACT_APP_BACKEND_BASE_URL;
  }
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname.includes("localhost") || hostname.includes("local")) {
      return `http://${hostname}:5100`;
    }
    return `${window.location.protocol}//${hostname}`;
  }
  return "http://localhost:5100";
};

/**
 * Get the full URL for an image
 * Handles both local paths and cloud storage URLs
 * @param imagePath - Relative path like "uploads/filename.jpg" or full URL
 * @param placeholder - Placeholder image URL to use if imagePath is empty (defaults to /logo512.png)
 * @returns Full URL to the image
 */
export const getImageUrl = (imagePath?: string | null, placeholder: string = "/logo512.png"): string => {
  if (!imagePath || imagePath.trim() === "") {
    return placeholder;
  }

  // If it's already a full URL (http/https), return as-is
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // If it's a cloud storage URL (stored in DB), return as-is
  // Cloud storage URLs are stored as full URLs in the database
  if (imagePath.includes("digitaloceanspaces.com") || imagePath.includes("cdn.")) {
    return imagePath;
  }

  // Handle local paths
  const backendUrl = getBackendUrl();
  const cleanPath = imagePath.startsWith("/") ? imagePath.slice(1) : imagePath;

  // Handle thumbnails
  if (cleanPath.startsWith("thumbnails/")) {
    const filename = cleanPath.replace("thumbnails/", "");
    return `${backendUrl}/api/thumbnails/${filename}`;
  }

  // Handle uploads
  if (cleanPath.startsWith("uploads/")) {
    const filename = cleanPath.replace("uploads/", "");
    return `${backendUrl}/api/uploads/${filename}`;
  }

  // Default to uploads
  return `${backendUrl}/api/uploads/${cleanPath}`;
};

/**
 * Get thumbnail URL (prefers thumbnails, falls back to original)
 * @param imagePath - Relative path like "uploads/filename.jpg" or full URL
 * @param placeholder - Placeholder image URL to use if imagePath is empty (defaults to /logo512.png)
 * @returns Full URL to the thumbnail or original image
 */
export const getThumbnailUrl = (imagePath?: string | null, placeholder: string = "/logo512.png"): string => {
  if (!imagePath || imagePath.trim() === "") {
    return placeholder;
  }

  // If it's already a full URL (http/https), check if it's cloud storage
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    // If it's a cloud storage URL, try to get thumbnail version
    if (imagePath.includes("digitaloceanspaces.com") || imagePath.includes("cdn.")) {
      // Try to replace "uploads/" with "thumbnails/" in the URL
      const thumbnailUrl = imagePath.replace("/uploads/", "/thumbnails/");
      // If the replacement didn't change anything, it might already be a thumbnail or different format
      return thumbnailUrl !== imagePath ? thumbnailUrl : imagePath;
    }
    return imagePath;
  }

  // Extract filename from local path
  const cleanPath = imagePath.startsWith("/") ? imagePath.slice(1) : imagePath;
  const filename = cleanPath.replace(/^(uploads|thumbnails)\//, "");

  // Return thumbnail URL
  const backendUrl = getBackendUrl();
  return `${backendUrl}/api/thumbnails/${filename}`;
};

/**
 * Get the original image URL (not thumbnail)
 * @param imagePath - Relative path like "uploads/filename.jpg" or full URL
 * @param placeholder - Placeholder image URL to use if imagePath is empty (defaults to /logo512.png)
 * @returns Full URL to the original image
 */
export const getOriginalImageUrl = (imagePath?: string | null, placeholder: string = "/logo512.png"): string => {
  if (!imagePath || imagePath.trim() === "") {
    return placeholder;
  }

  // If it's already a full URL, return as-is (might be cloud storage)
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    // If it's a thumbnail URL, convert to original
    if (imagePath.includes("/thumbnails/")) {
      return imagePath.replace("/thumbnails/", "/uploads/");
    }
    return imagePath;
  }

  // Handle local paths
  const backendUrl = getBackendUrl();
  const cleanPath = imagePath.startsWith("/") ? imagePath.slice(1) : imagePath;
  const filename = cleanPath.replace(/^(uploads|thumbnails)\//, "");

  return `${backendUrl}/api/uploads/${filename}`;
};

/**
 * Check if an image URL is from cloud storage
 * @param imagePath - Image path or URL
 * @returns True if it's a cloud storage URL
 */
export const isCloudStorageUrl = (imagePath?: string | null): boolean => {
  if (!imagePath) {
    return false;
  }
  return (
    imagePath.includes("digitaloceanspaces.com") ||
    imagePath.includes("cdn.") ||
    (imagePath.startsWith("https://") && !imagePath.includes("localhost"))
  );
};

/**
 * Image size presets for different use cases
 */
export enum ImagePresets {
  productCard = "productCard",
  productDetail = "productDetail",
  cartItem = "cartItem",
  checkoutReview = "checkoutReview",
}

/**
 * Get resized image URL using backend image resizing API
 * Falls back to regular image URL if cloud storage is enabled
 * @param imagePath - Relative path like "uploads/filename.jpg" or full URL
 * @param preset - Image size preset
 * @param placeholder - Placeholder image URL to use if imagePath is empty (defaults to /logo512.png)
 * @returns Full URL to the resized image
 */
export const getResizedImageUrl = (
  imagePath?: string | null,
  preset: ImagePresets = ImagePresets.productCard,
  placeholder: string = "/logo512.png"
): string => {
  if (!imagePath || imagePath.trim() === "") {
    return placeholder;
  }

  // If it's already a full URL (cloud storage), return as-is
  // Cloud storage URLs are already optimized and don't need resizing
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    if (isCloudStorageUrl(imagePath)) {
      return imagePath;
    }
  }

  // For local images, use backend resizing API
  const backendUrl = getBackendUrl();
  const cleanPath = imagePath.startsWith("/") ? imagePath.slice(1) : imagePath;
  
  // Extract filename
  const filename = cleanPath.replace(/^(uploads|thumbnails)\//, "");

  // Map presets to query parameters
  const presetParams: Record<ImagePresets, { width?: number; height?: number }> = {
    [ImagePresets.productCard]: { width: 400 },
    [ImagePresets.productDetail]: { width: 800 },
    [ImagePresets.cartItem]: { width: 150 },
    [ImagePresets.checkoutReview]: { width: 200 },
  };

  const params = presetParams[preset];
  const queryString = new URLSearchParams();
  if (params.width) queryString.set("w", params.width.toString());
  if (params.height) queryString.set("h", params.height.toString());

  return `${backendUrl}/api/images/${filename}?${queryString.toString()}`;
};
